/* eslint-disable @typescript-eslint/no-explicit-any */
import { ListPage } from '@components/molecules';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import { Box, Chip, Grid, Tooltip } from '@mui/material';
import API from '@apps/nnc/services/actionplanactions';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { JSX, useEffect, useState } from 'react';
import { NncSchema, NncSearchSchema } from '@apps/nnc/schemas/nnc';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import UnitAPI from '@apps/localization/services/unit';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { AutocompleteElement, TextFieldElement } from 'react-hook-form-mui';
import CircleIcon from '@mui/icons-material/Circle';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router';
import CountryAPI from '@apps/localization/services/country';
import FunctionalAreaAPI from '@apps/nnc/services/functionalarea';

export default function EntriesListPage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defaultFormContext = useForm<z.infer<z.ZodTypeAny>>();
  const start = defaultFormContext.watch('start_date', null);
  const end = defaultFormContext.watch('end_date', null);
  const [refetch, setRefresh] = useState(false);

  const opt = [
    {
      id: 1,
      label: t('Pending'),
    },
    {
      id: 2,
      label: t('On Going'),
    },
    {
      id: 3,
      label: t('Completed'),
    },
    {
      id: 4,
      label: t('Unfounded'),
    },
  ];

  const columns = [
    {
      field: 'reference_number',
      headerName: t('Action'),
      flex: 1,
    },
    {
      field: 'functional_area',
      headerName: t('Functional Area'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={'flex'} sx={{ height: '100%' }} alignItems="center">
            {params?.row?.functional_area?.label || ''}
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: 'origin',
      headerName: t('Origin'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const origins = params.row.action_plan?.failure_mode?.map(
          (i: any) => i.origin?.label,
        );
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {origins[0] || ''}
            {origins.length > 1 && (
              <Tooltip
                title={
                  <>
                    {origins.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{origins.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'description',
      headerName: t('Description'),
      flex: 2,
    },
    {
      field: 'responsible',
      headerName: t('Responsible'),
      flex: 1,
    },
    {
      field: 'due_date',
      headerName: t('Due Date'),
      flex: 1,
    },
    {
      field: 'duration',
      headerName: t('Duration'),
      width: 150,

      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={'flex'} sx={{ height: '100%' }} alignItems="center">
            <Box
              sx={{
                background: '#00d160',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 'bold',
                width: '80px',
                height: '30px',
              }}
            >
              {params?.row?.duration}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: t('Status'),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            color={
              params?.row.status?.id == 1
                ? 'info'
                : params?.row.status?.id == 2
                  ? 'warning'
                  : params?.row.status?.id == 3
                    ? 'success'
                    : 'error'
            }
            icon={<CircleIcon sx={{ color: '#ffffff80 !important' }} />}
            label={params?.row.status?.label}
          />
        );
      },
      flex: 1,
    },
    {
      field: 'actionss',
      headerName: t('Actions'),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <GridActionsCellItem
            key={`edit-${params?.row.id}`}
            icon={<EditIcon />}
            onClick={() =>
              navigate(
                `/nnc/actionplan/add-update/${params?.row.action_plan?.id}`,
              )
            }
            label="edit"
          />
        );
      },
      width: 90,
    },
  ];

  useEffect(() => {
    if (!start && !end) {
      setRefresh(!refetch);
    }
  }, [start, end]);

  return (
    <>
      <Box sx={{ position: 'relative', pb: 10 }}>
        <ListPage
          formcontext={defaultFormContext}
          schema={NncSchema}
          schemaSearch={NncSearchSchema}
          title={t('All Actions')}
          api={API}
          columns={columns}
          modelProps={modelProps}
          transformQueryParams={(params) => ({
            ...params,
          })}
          disableAdd
          disableDelete
          disableEdit
        >
          <Grid size={{ xs: 12, md: 2.8 }}>
            <TextFieldElement name="search" label={t('Search')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <DatePickerElement
              name="start_date"
              label={t('Start Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <DatePickerElement
              name="end_date"
              label={t('End Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AsyncAutocompleteElement
              api={CountryAPI.select}
              name="country"
              label={t('Country')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AsyncAutocompleteElement
              api={() =>
                UnitAPI.select({
                  is_active: 1,
                })
              }
              name="unit"
              label={t('Unit')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AsyncAutocompleteElement
              api={FunctionalAreaAPI.select}
              name="functional_area"
              label={t('Functional Area')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <TextFieldElement
              name="responsible"
              label={t('Responsible')}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AutocompleteElement
              name="status"
              label={t('Status')}
              multiple
              options={opt}
            />
          </Grid>
        </ListPage>
      </Box>
    </>
  );
}
